import { Ban, CheckCircle2, CircleSlash, PauseCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, type BadgeProps } from './badge';

/**
 * One mapping from a domain status to a colour, shared by both apps.
 *
 * Before this existed, `active` was a green pill in the portal and a green
 * pill in admin because two people happened to write the same class name. A
 * key's `revoked` was grey in one place and the same grey meant "unknown"
 * elsewhere. Colour is the fastest signal on these pages and it has to mean
 * one thing.
 */
export type Status =
  | 'active'
  | 'suspended'
  | 'closed'
  | 'revoked'
  | 'ok'
  | 'stale'
  | 'unknown';

const STATUS: Record<Status, { variant: BadgeProps['variant']; icon: ReactNode; label: string }> = {
  active: { variant: 'success', icon: <CheckCircle2 />, label: 'active' },
  ok: { variant: 'success', icon: <CheckCircle2 />, label: 'ok' },
  // Suspended is a deliberate staff action, not a fault — amber, not red, so
  // it does not compete with the genuinely broken states on the health page.
  suspended: { variant: 'warning', icon: <PauseCircle />, label: 'suspended' },
  stale: { variant: 'warning', icon: <PauseCircle />, label: 'stale' },
  closed: { variant: 'muted', icon: <CircleSlash />, label: 'closed' },
  revoked: { variant: 'muted', icon: <Ban />, label: 'revoked' },
  unknown: { variant: 'muted', icon: <CircleSlash />, label: 'unknown' },
};

export function StatusBadge({
  status,
  label,
  className,
}: {
  status: string;
  label?: string;
  className?: string;
}) {
  const entry = STATUS[status as Status] ?? STATUS.unknown;
  return (
    <Badge variant={entry.variant} className={className}>
      {entry.icon}
      {label ?? (status in STATUS ? entry.label : status)}
    </Badge>
  );
}
